import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@ui/Button';
import { Card, CardContent, CardTitle } from '@ui/Card';
import { useAuthStore } from '@stores/authStore';
import { useProjectStore } from '@stores/projectStore';
import type { Project } from '../types/api';

export const MainDashboard: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const {
    projects,
    isLoading,
    error,
    fetchProjects,
    deleteProject,
    clearError,
  } = useProjectStore();

  // 防御性编程：确保projects是数组
  const safeProjects = Array.isArray(projects) ? projects : [];

  // 每次路由变化（返回此页面）时刷新项目列表
  useEffect(() => {
    fetchProjects();
  }, [location.key, fetchProjects]);

  const projectTypeLabels: Record<string, string> = {
    image_recognition: '图像识别',
    voice_recognition: '语音识别',
    digit_recognition: '手写识别',
    digit_recognition_basic: '手写识别-基础',
    digit_recognition_enhanced: '手写识别-增强',
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            欢迎回来，{user?.username}！
          </h1>
          <p className="text-gray-600">继续你的AI学习之旅</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-red-600">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={clearError}
              >
                关闭
              </Button>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <CardTitle>我的项目</CardTitle>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600 mb-1">{projects.length}</p>
              <p className="text-sm text-gray-500">个AI项目</p>
            </CardContent>
          </Card>

          <Card className="p-6">
            <CardTitle>已训练模型</CardTitle>
            <CardContent>
              <p className="text-3xl font-bold text-green-600 mb-1">
                {safeProjects.filter((p) => p.is_trained).length}
              </p>
              <p className="text-sm text-gray-500">个可用模型</p>
            </CardContent>
          </Card>

          <Card className="p-6">
            <CardTitle>用户等级</CardTitle>
            <CardContent>
              <p className="text-3xl font-bold text-purple-600 mb-1">
                Lv.{user?.level || 1}
              </p>
              <p className="text-sm text-gray-500">继续成长</p>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button onClick={() => window.location.href = '/ai-select'} size="lg">
            <span className="mr-2">+</span> 创建新项目
          </Button>
        </div>

        {/* Projects List */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">我的项目</h2>
            {safeProjects.length > 0 && (
              <Button variant="outline" onClick={fetchProjects}>
                刷新
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          ) : safeProjects.length === 0 ? (
            <Card className="p-12 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">还没有项目</h3>
              <p className="text-gray-600 mb-4">创建你的第一个AI项目开始学习</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {safeProjects.map((project) => (
                <Card key={project.id} className="p-6 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {project.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {project.description || '暂无描述'}
                  </p>

                  <div className="mb-4">
                    <span className="text-sm text-gray-500">类型：</span>
                    <span className="text-sm font-medium text-gray-900 ml-1">
                      {projectTypeLabels[project.project_type] || project.project_type}
                    </span>
                  </div>

                  <div className="mb-4">
                    <span className="text-sm text-gray-500">状态：</span>
                    <span
                      className={`text-sm font-medium ml-1 ${
                        project.is_trained ? 'text-green-600' : 'text-yellow-600'
                      }`}
                    >
                      {project.is_trained ? '已训练' : '未训练'}
                    </span>
                  </div>

                  {project.is_trained && (
                    <div className="mb-4">
                      <span className="text-sm text-gray-500">准确率：</span>
                      <span className="text-sm font-medium text-gray-900 ml-1">
                        {(project.training_accuracy * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!project.is_trained}
                        title={project.is_trained ? '测试模型' : '请先编辑项目并完成采样训练'}
                        onClick={() => {
                          const isDigit = project.project_type === 'digit_recognition_basic' || project.project_type === 'digit_recognition_enhanced';
                          window.location.href = isDigit
                            ? `/digit-testing?projectId=${project.id}`
                            : `/testing?projectId=${project.id}`;
                        }}
                      >
                        测试
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const isDigit = project.project_type === 'digit_recognition_basic' || project.project_type === 'digit_recognition_enhanced';
                          window.location.href = isDigit
                            ? `/digit-sampling?projectId=${project.id}`
                            : `/image-config?projectId=${project.id}`;
                        }}
                      >
                        编辑
                      </Button>
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (window.confirm(`确定要删除项目「${project.name}」吗？删除后不可恢复。`)) {
                          deleteProject(String(project.id));
                        }
                      }}
                    >
                      删除
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
